
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import ListHeader from '@/components/List/ListHeader.vue';
import { init } from '@/db';
import { Ripple } from 'vuetify/lib/directives';
import {
  mdiPencil,
  mdiDelete,
  mdiMagnify,
  mdiSelectGroup,
  mdiCalendar,
  mdiPlus,
} from '@mdi/js';
import { DateTime } from 'luxon';
import cuid from 'cuid';
import { debounce } from 'lodash-es';

export default {
  components: {
    ConfirmDialog,
    ListHeader,
  },

  directives: {
    Ripple,
  },
  data: () => ({
    categories: [],
    total: 10,
    defaultLimit: -1,
    DateTime,
    newDueMenu: false,
    icons: {
      mdiPencil,
      mdiDelete,
      mdiMagnify,
      mdiSelectGroup,
      mdiCalendar,
      mdiPlus,
    },
    dialog: false,
    deleteDialog: false,
    headers: [
      { value: 'done', align: 'start', width: 1 },
      {
        text: 'Name',
        value: 'name',
        align: 'start',
        groupable: false,
      },
      { text: 'Category', value: 'category', groupable: true },
      {
        value: 'actions',
        sortable: false,
        filterable: false,
        align: 'right',
        width: 'auto',
      },
    ],
    items: [],
    editedIndex: -1,
    editedItem: {
      id: `${cuid()}`,
      name: '',
      due: DateTime.local().toISODate(),
      category: '',
      done: false,
    },
  }),
  async fetch() {
    this.$db = await init({
      remote: this.$warehouse.get('dbRemote') || this.$config.DB_REMOTE,
      username: this.$warehouse.get('dbUsername') || this.$config.DB_USERNAME,
      password: this.$warehouse.get('dbPassword') || this.$config.DB_PASSWORD,
    });
    this.collection = this.$db.shopping.find({
      selector: {
        name: { $regex: `.*${this.search}.*` },
      },
    });
    this.sub = this.collection.$.subscribe((results = []) => {
      this.total = results.length;
      this.items = results;
    });
  },
  head() {
    return {
      title: 'Shopping',
    };
  },
  computed: {
    pages() {
      const pages = Math.ceil(this.total / this.limit);
      return pages || 1;
    },
    page: {
      get() {
        const page = parseInt(this.$route.query.page, 10) || 1;
        // set page to last page if page is larger than last page
        if (page <= this.pages) {
          return page;
        } else {
          return this.pages;
        }
      },
      set(page) {
        this.$router.push({ query: this.query({ page }) });
      },
    },
    limit: {
      get() {
        const limit =
          parseInt(this.$route.query.limit, 10) || this.defaultLimit;
        return limit === -1 ? this.total : limit;
      },
      set(limit) {
        this.$router.push({ query: this.query({ limit }) });
      },
    },
    direction: {
      get() {
        return this.$route.query.direction || 'desc';
      },
      set(direction) {
        this.$router.push({ query: this.query({ direction }) });
      },
    },
    search: {
      get() {
        return this.$route.query.search || '';
      },
      set(search) {
        this.$router.push({ query: this.query({ search }) });
      },
    },
    sortBy: {
      get() {
        const sortBy = this.$route.query.sortBy ?? 'category';
        return this.items.length ? sortBy : null;
      },
      set(sortBy) {
        this.$router.push({ query: this.query({ sortBy }) });
      },
    },
    groupBy: {
      get() {
        const groupBy = this.$route.query.groupBy ?? 'category';
        return this.items.length ? groupBy : null;
      },
      set(groupBy) {
        this.$router.push({ query: this.query({ groupBy }) });
      },
    },
    view: {
      get() {
        return this.$route.query.view || this.layout;
      },
      set(view) {
        this.$router.push({ query: this.query({ view }) });
      },
    },
  },
  fetchOnServer: false,
  watch: {
    dialog(val) {
      val || this.close();
    },
    '$route.query': debounce(function () {
      this.$fetch();
    }, 500),
  },
  beforeDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  },
  methods: {
    defaultItem: () => ({
      id: `${cuid()}`,
      name: '',
      category: '',
      due: DateTime.local().toISODate(),
      done: false,
    }),

    editItem(item) {
      this.editedItem = item.toJSON();
      this.dialog = true;
    },
    async deleteItem(item) {
      await item.remove();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem());
        this.editedIndex = -1;
      });
    },

    create(item = this.defaultItem()) {
      this.editedItem = item;
      this.dialog = true;
    },

    async save(item) {
      await this.$db.shopping.upsert(item);
      this.close();
    },

    clear(items) {
      this.collection.remove();
    },

    async bulkUpdateAttribute({ items, key, value }) {
      await Promise.all(items.map((item) => item.atomicSet(key, value)));
    },

    query({
      sortBy = this.sortBy,
      groupBy = this.groupBy,
      page = this.page,
      limit = this.limit,
      direction = this.direction,
      search = this.search,
      view = this.view,
    }) {
      // sorted query string for more cache hits
      return Object.fromEntries(
        Object.entries({
          sortBy: sortBy && sortBy !== 'category' ? sortBy : undefined,
          groupBy: groupBy && groupBy !== 'category' ? groupBy : undefined,
          search: search && search !== '' ? search : undefined,
          view: view && view !== this.layout ? view : undefined,
          direction: direction !== 'desc' ? direction : undefined,
          page: page !== 1 ? page : undefined,
          limit:
            limit !== this.defaultLimit && limit !== this.total
              ? limit
              : undefined,
        }).sort(),
      );
    },
  },
};
